import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import useAuth from "../hooks/useAuth";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { ArrowDown, ArrowUp, Pencil, Trash2 } from "lucide-react";
import { Table, Button, Modal, Form, Spinner } from "react-bootstrap";
import { MATERIAL_CATEGORY_OPTIONS, isPrintsEmbroideryCategory, MATERIAL_COLOR_NA_PLACEHOLDER } from "../helpers/materialCategories";
import {
    getMaterialPropertyKeySelectOptions,
    materialPropertyPairsSummary,
    materialPropertyPairsSearchBlob,
    normalizePropertyPairsPayload,
} from "../helpers/materialPropertyPairs";
import {
    defaultMaterialFormUnitForCategory,
    isStandardMaterialUnitSelectValue,
    materialUnitSelectValue,
} from "../helpers/materialUnits";
import { getWindingMetersFromPropertyPairs } from "../helpers/materialWindingMeters";
import {
    TECH_CARD_AUTOCOMPLETE_COMPONENTS_PROPS,
    TECH_CARD_AUTOCOMPLETE_LISTBOX_SX,
    TECH_CARD_MUI_FIELD_SX,
} from "../helpers/muiTechCardEditorFieldSx";

/** Ключ властивості: вигляд як у Autocomplete у рядку властивостей. */
const MATERIAL_PROPERTY_KEY_AUTOCOMPLETE_SX = {
    ...TECH_CARD_MUI_FIELD_SX,
    width: "100%",
    minWidth: 0,
    "& .MuiOutlinedInput-root": {
        minHeight: 32,
        fontSize: "0.8125rem",
    },
};

const MATERIAL_UNIT_FORM_SELECT_EXTRA = "упаковок";

function MaterialUnitFormSelect({ value, onChange }) {
    const v = String(value ?? "");
    const isKnown =
        isStandardMaterialUnitSelectValue(v) || v === MATERIAL_UNIT_FORM_SELECT_EXTRA;
    const showCustom = Boolean(v) && !isKnown;
    return (
        <Form.Select value={v} onChange={onChange}>
            {showCustom ? <option value={v}>{v}</option> : null}
            <option value="м">метри</option>
            <option value="шт">шт</option>
            <option value="кг">кг</option>
            <option value={MATERIAL_UNIT_FORM_SELECT_EXTRA}>упаковок</option>
        </Form.Select>
    );
}

/** Категорія рядка з populate typeId або з довідника types. */
function materialRowCategory(m, typesList) {
    if (m?.typeId && typeof m.typeId === "object" && m.typeId.category) {
        return String(m.typeId.category);
    }
    const tid = m?.typeId?._id ?? m?.typeId;
    if (tid && Array.isArray(typesList)) {
        const hit = typesList.find((t) => String(t._id) === String(tid));
        if (hit?.category) {
            return String(hit.category);
        }
    }
    return "FABRIC";
}

function newPropertyRowId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function MaterialPropertyKeySelect({ value, category, unit, onChange, onBlur, style }) {
    const baseOptions = useMemo(
        () => getMaterialPropertyKeySelectOptions(category, unit),
        [category, unit]
    );
    const v = String(value ?? "");
    const options = useMemo(() => {
        if (!v) {
            return baseOptions;
        }
        const allowed = new Set(baseOptions.map((o) => o.value));
        if (!allowed.has(v)) {
            return [{ value: v, label: `${v} (збережено)` }, ...baseOptions];
        }
        return baseOptions;
    }, [baseOptions, v]);

    const selected = useMemo(() => {
        if (!v) {
            return null;
        }
        return options.find((o) => o.value === v) ?? null;
    }, [v, options]);

    return (
        <div className="material-property-key-autocomplete-root" style={style}>
            <Autocomplete
                size="small"
                sx={MATERIAL_PROPERTY_KEY_AUTOCOMPLETE_SX}
                componentsProps={TECH_CARD_AUTOCOMPLETE_COMPONENTS_PROPS}
                ListboxProps={{
                    sx: TECH_CARD_AUTOCOMPLETE_LISTBOX_SX,
                    style: { maxHeight: 280 },
                }}
                options={options}
                getOptionLabel={(o) => (o && o.label != null ? String(o.label) : "")}
                isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
                value={selected}
                onChange={(_, newVal) => onChange(newVal ? newVal.value : "")}
                onBlur={onBlur}
                blurOnSelect
                clearOnBlur={false}
                aria-label="Ключ властивості"
                filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    if (!q) {
                        return opts;
                    }
                    return opts.filter(
                        (o) =>
                            String(o.label || "")
                                .toLowerCase()
                                .includes(q) ||
                            String(o.value || "")
                                .toLowerCase()
                                .includes(q)
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        hiddenLabel
                        placeholder="Оберіть ключ"
                        size="small"
                        InputProps={{
                            ...params.InputProps,
                            notched: false,
                        }}
                    />
                )}
            />
        </div>
    );
}

function propertyRowsFromServerPairs(list) {
    const arr = Array.isArray(list) ? list : [];
    return arr.map((p) => ({
        id: newPropertyRowId(),
        key: String(p.key ?? ""),
        value: String(p.value ?? ""),
    }));
}

/** Рядок таблиці: пари ключ : значення, збереження по blur та після видалення рядка. */
function MaterialPropertyPairsTableCell({ material, types, onUpdated }) {
    const [rows, setRows] = useState(() => propertyRowsFromServerPairs(material.propertyPairs));
    const rowsRef = useRef(rows);
    rowsRef.current = rows;

    const category = materialRowCategory(material, types);

    useEffect(() => {
        setRows(propertyRowsFromServerPairs(material.propertyPairs));
    }, [material._id, JSON.stringify(material.propertyPairs)]);

    const persist = () => {
        const payload = normalizePropertyPairsPayload(
            rowsRef.current.map(({ key, value }) => ({ key, value }))
        );
        axios
            .patch(`/api/materials/${material._id}`, { propertyPairs: payload })
            .then((res) => onUpdated(res.data))
            .catch((err) => console.error(err));
    };

    const addRow = () => {
        setRows((r) => [...r, { id: newPropertyRowId(), key: "", value: "" }]);
    };

    const removeRow = (id) => {
        setRows((r) => {
            const next = r.filter((x) => x.id !== id);
            const payload = normalizePropertyPairsPayload(next.map(({ key, value }) => ({ key, value })));
            axios
                .patch(`/api/materials/${material._id}`, { propertyPairs: payload })
                .then((res) => onUpdated(res.data))
                .catch((err) => console.error(err));
            return next;
        });
    };

    const updateRow = (id, field, value) => {
        setRows((r) => r.map((x) => (x.id === id ? { ...x, [field]: value } : x)));
    };

    return (
        <div className="material-property-pairs-table-cell">
            {rows.map((row) => (
                <div
                    key={row.id}
                    className="material-property-pairs-row d-flex align-items-center mb-1 material-property-pairs-row--table"
                >
                    <MaterialPropertyKeySelect
                        value={row.key}
                        category={category}
                        unit={material.unit}
                        onChange={(keyVal) => updateRow(row.id, "key", keyVal)}
                        onBlur={persist}
                        style={{
                            flex: "0 0 auto",
                            width: "5.55rem",
                            minWidth: "5.35rem",
                            maxWidth: "5.85rem",
                        }}
                    />
                    <span className="text-muted flex-shrink-0 material-property-pairs-colon">:</span>
                    <Form.Control
                        size="sm"
                        htmlSize={Math.min(36, Math.max(2, row.value.length))}
                        placeholder="Значення"
                        value={row.value}
                        onChange={(e) => updateRow(row.id, "value", e.target.value)}
                        onBlur={persist}
                        className="text-body material-property-pairs-value-input material-property-pairs-value-input--table"
                    />
                    <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        className="px-1 flex-shrink-0 lh-1 material-property-pairs-remove-btn"
                        title="Прибрати"
                        aria-label="Прибрати властивість"
                        onClick={() => removeRow(row.id)}
                    >
                        ×
                    </Button>
                </div>
            ))}
            <Button
                type="button"
                variant="outline-primary"
                size="sm"
                className="mt-1 py-0 lh-sm"
                onClick={addRow}
            >
                + властивість
            </Button>
        </div>
    );
}

/** Модалки: керований список пар з id для стабільних ключів React. */
function PropertyPairsModalEditor({ rows, onChange, category, unit }) {
    const add = () => onChange([...rows, { id: newPropertyRowId(), key: "", value: "" }]);
    const remove = (id) => onChange(rows.filter((r) => r.id !== id));
    const setCell = (id, field, val) =>
        onChange(rows.map((r) => (r.id === id ? { ...r, [field]: val } : r)));

    return (
        <div>
            {rows.map((r) => (
                <div key={r.id} className="d-flex gap-1 align-items-center mb-2">
                    <MaterialPropertyKeySelect
                        value={r.key}
                        category={category}
                        unit={unit}
                        onChange={(keyVal) => setCell(r.id, "key", keyVal)}
                        style={{
                            flex: "0 0 auto",
                            width: "6.5rem",
                            minWidth: "6.25rem",
                            maxWidth: "7rem",
                        }}
                    />
                    <span className="text-muted">:</span>
                    <Form.Control
                        size="sm"
                        placeholder="Значення"
                        value={r.value}
                        onChange={(e) => setCell(r.id, "value", e.target.value)}
                        className="flex-grow-1 min-w-0"
                    />
                    <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        className="flex-shrink-0"
                        onClick={() => remove(r.id)}
                        aria-label="Видалити рядок"
                    >
                        ×
                    </Button>
                </div>
            ))}
            <Button type="button" variant="outline-primary" size="sm" onClick={add}>
                + властивість
            </Button>
        </div>
    );
}

export default function MaterialsPage() {
    const { user } = useAuth();

    const [materials, setMaterials] = useState([]);
    const [types, setTypes] = useState([]);

    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);

    const [editMaterial, setEditMaterial] = useState(null);
    const [editForm, setEditForm] = useState({
        typeId: "",
        typeName: "",
        color: "",
        unit: "м",
        propertyPairRows: [],
        note: "",
        unitPriceBasis: "stock",
    });

    const [form, setForm] = useState({
        typeName: "",
        color: "",
        unit: "м",
        propertyPairRows: [],
        note: "",
        priceUAH: "",
        priceUSD: "",
    });

    /** Фільтр списку номенклатури в модалці «Новий матеріал». */
    const [createTypeCategory, setCreateTypeCategory] = useState("FABRIC");
    /** Фільтр таблиці: усі або одна з категорій MaterialType. */
    const [listCategoryFilter, setListCategoryFilter] = useState("all");
    const [filterQ, setFilterQ] = useState("");
    const [sortKey, setSortKey] = useState("nomenclature");
    const [sortDir, setSortDir] = useState("asc");

    const toggleSort = (key) => {
        if (sortKey !== key) {
            setSortKey(key);
            setSortDir("asc");
        } else {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        }
    };

    const sortArrows = (key) =>
        sortKey === key ? (
            sortDir === "asc" ? (
                <ArrowUp className="ms-1 opacity-75" size={12} aria-hidden />
            ) : (
                <ArrowDown className="ms-1 opacity-75" size={12} aria-hidden />
            )
        ) : null;

    useEffect(() => {

        fetchMaterials();
        fetchTypes();

    }, []);

    const typesForCreate = useMemo(
        () => types.filter((t) => String(t.category || "FABRIC") === createTypeCategory),
        [types, createTypeCategory]
    );

    const displayedMaterials = useMemo(() => {
        let rows = materials;
        if (listCategoryFilter !== "all") {
            rows = rows.filter(
                (m) => materialRowCategory(m, types) === listCategoryFilter
            );
        }
        const q = filterQ.trim().toLowerCase();
        if (q) {
            rows = rows.filter((m) => {
                const name = String(m.typeId?.name || "").toLowerCase();
                const color = String(m.color || "").toLowerCase();
                const unit = String(m.unit || "").toLowerCase();
                const note = String(m.note || "").toLowerCase();
                const propsBlob = materialPropertyPairsSearchBlob(m.propertyPairs);
                const hay = `${name} ${color} ${unit} ${note} ${propsBlob}`;
                return hay.includes(q);
            });
        }
        const dir = sortDir === "asc" ? 1 : -1;
        const sorted = [...rows].sort((a, b) => {
            let va;
            let vb;
            switch (sortKey) {
                case "nomenclature":
                    va = String(a.typeId?.name || "").toLowerCase();
                    vb = String(b.typeId?.name || "").toLowerCase();
                    break;
                case "color":
                    va = String(a.color || "").toLowerCase();
                    vb = String(b.color || "").toLowerCase();
                    break;
                case "unit":
                    va = String(a.unit || "").toLowerCase();
                    vb = String(b.unit || "").toLowerCase();
                    break;
                case "properties":
                    va = materialPropertyPairsSummary(a.propertyPairs).toLowerCase();
                    vb = materialPropertyPairsSummary(b.propertyPairs).toLowerCase();
                    break;
                case "comment":
                    va = String(a.note || "").toLowerCase();
                    vb = String(b.note || "").toLowerCase();
                    break;
                default:
                    return 0;
            }
            if (va < vb) {
                return -1 * dir;
            }
            if (va > vb) {
                return 1 * dir;
            }
            return String(a._id).localeCompare(String(b._id));
        });
        return sorted;
    }, [
        materials,
        types,
        listCategoryFilter,
        filterQ,
        sortKey,
        sortDir,
    ]);

    const editFormWindingMeters = useMemo(() => {
        const pairs = normalizePropertyPairsPayload(
            editForm.propertyPairRows.map(({ key, value }) => ({ key, value }))
        );
        return getWindingMetersFromPropertyPairs(pairs);
    }, [editForm.propertyPairRows]);

    const fetchMaterials = async () => {

        try {

            const res = await axios.get("/api/materials");

            setMaterials(res.data);

        } catch (err) {

            console.error(err);

        }

        setLoading(false);

    };

    const fetchTypes = async () => {

        const res = await axios.get("/api/material-types");

        setTypes(res.data);

    };

    const createMaterial = async () => {
        const name = String(form.typeName || "").trim();
        if (!name) {
            window.alert(
                "Вкажіть конкретну назву матеріалу (наприклад «Блискавка №18», а не лише «блискавки»)."
            );
            return;
        }
        try {
            const payload = {
                typeName: name,
                category: createTypeCategory,
                color: isPrintsEmbroideryCategory(createTypeCategory)
                    ? MATERIAL_COLOR_NA_PLACEHOLDER
                    : String(form.color || "").trim(),
                unit: form.unit,
                propertyPairs: normalizePropertyPairsPayload(
                    form.propertyPairRows.map(({ key, value }) => ({ key, value }))
                ),
                note: form.note,
            };
            const uahRaw = String(form.priceUAH ?? "").trim().replace(",", ".");
            const usdRaw = String(form.priceUSD ?? "").trim().replace(",", ".");
            if (uahRaw !== "") {
                const uah = Number(uahRaw);
                if (Number.isFinite(uah) && uah >= 0) {
                    payload.priceUAH = uah;
                }
            }
            if (usdRaw !== "") {
                const usd = Number(usdRaw);
                if (Number.isFinite(usd) && usd >= 0) {
                    payload.priceUSD = usd;
                }
            }
            await axios.post("/api/materials", payload);
            setShowCreate(false);
            setForm({
                typeName: "",
                color: "",
                unit: defaultMaterialFormUnitForCategory(createTypeCategory),
                propertyPairRows: [],
                note: "",
                priceUAH: "",
                priceUSD: "",
            });
            await fetchMaterials();
            await fetchTypes();
        } catch (e) {
            window.alert(
                e.response?.data?.message || e.message || "Не вдалося зберегти"
            );
        }
    };

    const openEditMaterial = (m) => {
        setEditMaterial(m);
        setEditForm({
            typeId: String(m.typeId?._id || m.typeId || ""),
            typeName: m.typeId?.name != null ? String(m.typeId.name) : "",
            color: m.color != null ? String(m.color) : "",
            unit: materialUnitSelectValue(m.unit),
            propertyPairRows: propertyRowsFromServerPairs(m.propertyPairs),
            note: m.note != null ? String(m.note) : "",
            unitPriceBasis: m.unitPriceBasis === "bobbin" ? "bobbin" : "stock",
        });
    };

    const closeEditMaterial = () => {
        setEditMaterial(null);
    };

    const saveEditMaterial = async () => {
        if (!editMaterial) {
            return;
        }
        if (!String(editForm.typeId || "").trim()) {
            window.alert("У матеріалу не задано тип номенклатури. Оновіть сторінку або зверніться до адміністратора.");
            return;
        }
        const tid = String(editForm.typeId).trim();
        const trimmedTypeName = String(editForm.typeName || "").trim();
        if (!trimmedTypeName) {
            window.alert("Вкажіть назву номенклатури в довіднику типів.");
            return;
        }
        try {
            const selectedType = types.find((t) => String(t._id) === tid);
            const prevName = selectedType ? String(selectedType.name || "").trim() : "";
            if (trimmedTypeName !== prevName) {
                await axios.patch(`/api/material-types/${tid}`, { name: trimmedTypeName });
                setTypes((prev) =>
                    prev.map((t) => (String(t._id) === tid ? { ...t, name: trimmedTypeName } : t))
                );
                setMaterials((prev) =>
                    prev.map((mm) => {
                        const mid = String(mm.typeId?._id || mm.typeId || "");
                        if (mid !== tid || typeof mm.typeId !== "object" || !mm.typeId) {
                            return mm;
                        }
                        return { ...mm, typeId: { ...mm.typeId, name: trimmedTypeName } };
                    })
                );
            }

            const pairsNorm = normalizePropertyPairsPayload(
                editForm.propertyPairRows.map(({ key, value }) => ({ key, value }))
            );
            const w = getWindingMetersFromPropertyPairs(pairsNorm);
            const unitStr = String(editForm.unit || "").trim().toLowerCase();
            const unitPriceBasis =
                editForm.unitPriceBasis === "bobbin" &&
                (unitStr === "м" || unitStr === "m") &&
                w != null &&
                w > 0
                    ? "bobbin"
                    : "stock";

            const payload = {
                typeId: editForm.typeId,
                color: isPrintsEmbroideryCategory(materialRowCategory(editMaterial, types))
                    ? MATERIAL_COLOR_NA_PLACEHOLDER
                    : String(editForm.color || "").trim(),
                unit: String(editForm.unit || "").trim(),
                propertyPairs: pairsNorm,
                note: String(editForm.note || "").trim() || null,
                unitPriceBasis,
            };
            const res = await axios.patch(`/api/materials/${editMaterial._id}`, payload);
            setMaterials((prev) =>
                prev.map((mm) =>
                    String(mm._id) === String(editMaterial._id) ? res.data : mm
                )
            );
            closeEditMaterial();
        } catch (e) {
            window.alert(
                e.response?.data?.message || e.message || "Не вдалося зберегти"
            );
        }
    };

    const confirmDeleteMaterial = async (m) => {
        const cat = materialRowCategory(m, types);
        const label = isPrintsEmbroideryCategory(cat)
            ? String(m.typeId?.name || "—").trim()
            : `${m.typeId?.name || "—"} / ${m.color || "—"}`.trim();
        if (
            !window.confirm(
                `Видалити матеріал?\n\n${label}\n\nМожливо лише якщо залишок 0 і немає закупівель, рухів чи техкарт з цим матеріалом.`
            )
        ) {
            return;
        }
        const tryDelete = async (force) => {
            await axios.delete(`/api/materials/${m._id}`, {
                ...(force ? { params: { force: "true" } } : {}),
                withCredentials: true,
            });
        };
        try {
            await tryDelete(false);
            setMaterials((prev) => prev.filter((mm) => String(mm._id) !== String(m._id)));
            if (editMaterial && String(editMaterial._id) === String(m._id)) {
                closeEditMaterial();
            }
        } catch (e) {
            const status = e.response?.status;
            const msg = e.response?.data?.message || e.message || "Не вдалося видалити";
            if (
                status === 409 &&
                user?.role === "admin" &&
                window.confirm(
                    `${msg}\n\nПримусово видалити цей матеріал як адміністратор?\nБуде очищено історію руху, рядки закупівель і норми в техкарті з цим матеріалом. Дія незворотна.`
                )
            ) {
                try {
                    await tryDelete(true);
                    setMaterials((prev) =>
                        prev.filter((mm) => String(mm._id) !== String(m._id))
                    );
                    if (editMaterial && String(editMaterial._id) === String(m._id)) {
                        closeEditMaterial();
                    }
                } catch (e2) {
                    window.alert(
                        e2.response?.data?.message ||
                            e2.message ||
                            "Не вдалося видалити примусово"
                    );
                }
            } else {
                window.alert(msg);
            }
        }
    };

    if (loading) {

        return (
            <div className="text-center mt-5">
                <Spinner animation="border"/>
            </div>
        );

    }

    return (

        <div className="production-tab-panel tab-content">

            <div className="mb-3">
                <h5 className="mb-1">Матеріали</h5>

            <p className="text-muted small mb-2">
                <strong>Лише картка матеріалу (без зміни залишку).</strong> Щоб збільшити залишок,
                оформіть закупівлю на вкладці «Закупки» або оприбуткуйте на складі без закупівлі
                (вкладка «Склад», кнопка «+»).
            </p>
            <p className="text-muted small mb-2">
                Категорія <strong>«Накати та вишивка»</strong> — накати/вишивка на виробах; облік у{" "}
                <strong>шт</strong>, колір у картці не вказується (лише назва позиції та залишок).
            </p>

            <p className="text-muted small mb-2">
                Для <strong>тканини на складі в кг</strong> у властивостях можна задати ключ{" "}
                <strong>«Вага 1 м.п.»</strong> і числове значення (кг/м.п.) — воно використовується в техкарті
                для норми «пог. м → кг».
            </p>
            <p className="text-muted small mb-2">
                <strong>Властивості</strong> — ключ зі списку (Розмір, Тип, Вага; для тканини в кг також Вага
                1 м.п.) і текстове значення. <strong>Колір</strong> — окремою колонкою. Зміни в таблиці
                зберігаються при виході з поля (blur).
            </p>

                <Form.Label htmlFor="materials-filter-q" className="visually-hidden">
                    Пошук матеріалів
                </Form.Label>
                <div
                    className="d-flex flex-nowrap align-items-stretch gap-2"
                    style={{ minWidth: 0 }}
                >
                    <Form.Control
                        id="materials-filter-q"
                        className="flex-grow-1"
                        style={{ minWidth: 0 }}
                        value={filterQ}
                        onChange={(e) => setFilterQ(e.target.value)}
                        placeholder="Назва, колір, од., властивості, коментар…"
                        aria-label="Пошук матеріалів"
                    />
                    <Form.Select
                        size="sm"
                        className="flex-shrink-0 align-self-stretch"
                        style={{ width: "auto", minWidth: "11rem", maxWidth: "14rem" }}
                        value={listCategoryFilter}
                        onChange={(e) => setListCategoryFilter(e.target.value)}
                        aria-label="Категорія в списку"
                        title="Категорія"
                    >
                        <option value="all">Усі категорії</option>
                        {MATERIAL_CATEGORY_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </Form.Select>
                    <Button
                        type="button"
                        variant="primary"
                        className="px-3 flex-shrink-0 align-self-stretch"
                        onClick={() => {
                            setCreateTypeCategory("FABRIC");
                            setForm({
                                typeName: "",
                                color: "",
                                unit: defaultMaterialFormUnitForCategory("FABRIC"),
                                propertyPairRows: [],
                                note: "",
                                priceUAH: "",
                                priceUSD: "",
                            });
                            setShowCreate(true);
                        }}
                        title="Додати матеріал"
                        aria-label="Додати матеріал"
                    >
                        +
                    </Button>
                </div>
            </div>

            <div className="materials-page-table-wrap materials-hub-table-scroll border rounded overflow-hidden mb-2">
            <Table
                bordered
                hover
                size="sm"
                className="materials-hub-table materials-page-table tech-card-materials-editor-table align-middle mb-0"
            >

                <thead className="table-light">

                <tr>
                    <th scope="col" className="materials-page-col-num text-nowrap">
                        №
                    </th>
                    <th
                        role="columnheader"
                        scope="col"
                        className="materials-page-col-name user-select-none text-nowrap"
                        aria-sort={
                            sortKey === "nomenclature"
                                ? sortDir === "asc"
                                    ? "ascending"
                                    : "descending"
                                : "none"
                        }
                    >
                        <button
                            type="button"
                            className="btn btn-link btn-sm text-decoration-none p-0 text-body materials-page-sort-btn py-0 lh-1"
                            onClick={() => toggleSort("nomenclature")}
                            aria-label="Сортувати за колонкою «Назва»"
                        >
                            <span className="fw-semibold">Назва</span>
                            {sortArrows("nomenclature")}
                        </button>
                    </th>
                    <th
                        role="columnheader"
                        scope="col"
                        className="materials-page-col-color user-select-none"
                        aria-sort={
                            sortKey === "color"
                                ? sortDir === "asc"
                                    ? "ascending"
                                    : "descending"
                                : "none"
                        }
                    >
                        <button
                            type="button"
                            className="btn btn-link btn-sm text-decoration-none p-0 text-body materials-page-sort-btn py-0 lh-1"
                            onClick={() => toggleSort("color")}
                            aria-label="Сортувати за колонкою «Колір»"
                        >
                            <span className="fw-semibold">Колір</span>
                            {sortArrows("color")}
                        </button>
                    </th>
                    <th
                        role="columnheader"
                        scope="col"
                        className="materials-page-col-unit user-select-none"
                        aria-sort={
                            sortKey === "unit"
                                ? sortDir === "asc"
                                    ? "ascending"
                                    : "descending"
                                : "none"
                        }
                    >
                        <button
                            type="button"
                            className="btn btn-link btn-sm text-decoration-none p-0 text-body materials-page-sort-btn py-0 lh-1"
                            onClick={() => toggleSort("unit")}
                            aria-label="Сортувати за колонкою «Од.»"
                        >
                            <span className="fw-semibold">Од.</span>
                            {sortArrows("unit")}
                        </button>
                    </th>
                    <th
                        role="columnheader"
                        scope="col"
                        className="materials-page-col-props user-select-none"
                        aria-sort={
                            sortKey === "properties"
                                ? sortDir === "asc"
                                    ? "ascending"
                                    : "descending"
                                : "none"
                        }
                    >
                        <button
                            type="button"
                            className="btn btn-link btn-sm text-decoration-none p-0 text-body materials-page-sort-btn py-0 lh-1"
                            onClick={() => toggleSort("properties")}
                            aria-label="Сортувати за колонкою «Властивості»"
                        >
                            <span className="fw-semibold">Властивості</span>
                            {sortArrows("properties")}
                        </button>
                    </th>
                    <th
                        role="columnheader"
                        scope="col"
                        className="materials-page-col-comment user-select-none"
                        aria-sort={
                            sortKey === "comment"
                                ? sortDir === "asc"
                                    ? "ascending"
                                    : "descending"
                                : "none"
                        }
                    >
                        <button
                            type="button"
                            className="btn btn-link btn-sm text-decoration-none p-0 text-body materials-page-sort-btn py-0 lh-1"
                            onClick={() => toggleSort("comment")}
                            aria-label="Сортувати за колонкою «Коментар»"
                        >
                            <span className="fw-semibold">Коментар</span>
                            {sortArrows("comment")}
                        </button>
                    </th>
                    <th scope="col" className="materials-page-col-actions text-nowrap">
                        <span className="fw-semibold">Дії</span>
                    </th>
                </tr>

                </thead>

                <tbody>

                {displayedMaterials.map((m, listIdx) => {

                    return (

                    <tr key={m._id}>
                        <td className="text-muted materials-page-col-num">
                            {listIdx + 1}
                        </td>

                        <td className="materials-page-col-name text-nowrap">{m.typeId?.name}</td>

                        <td className="materials-page-col-color">
                            {isPrintsEmbroideryCategory(materialRowCategory(m, types)) ? (
                                <span className="text-muted">—</span>
                            ) : (
                                m.color
                            )}
                        </td>

                        <td className="materials-page-col-unit text-nowrap">{m.unit}</td>

                        <td className="materials-page-col-props align-top materials-page-td-compact">
                            <MaterialPropertyPairsTableCell
                                material={m}
                                types={types}
                                onUpdated={(updated) => {
                                    setMaterials((prev) =>
                                        prev.map((mm) =>
                                            String(mm._id) === String(updated._id) ? updated : mm
                                        )
                                    );
                                }}
                            />
                        </td>

                        <td className="materials-page-col-comment">{m.note}</td>

                        <td
                            className="materials-page-col-actions align-middle py-0 ps-1"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="materials-page-action-btns">
                                <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="p-1 text-decoration-none border-0 shadow-none"
                                    style={{ color: "#212529" }}
                                    title="Редагувати"
                                    aria-label="Редагувати матеріал"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openEditMaterial(m);
                                    }}
                                >
                                    <Pencil size={16} aria-hidden />
                                </Button>
                                <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="p-1 text-danger"
                                    title="Видалити"
                                    aria-label="Видалити матеріал"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        confirmDeleteMaterial(m);
                                    }}
                                >
                                    <Trash2 size={16} aria-hidden />
                                </Button>
                            </div>
                        </td>

                    </tr>

                    );
                })}

                </tbody>

            </Table>
            </div>
            {!displayedMaterials.length && (
                <p className="text-muted small mb-0 mt-2">Нічого не знайдено.</p>
            )}

            <Modal show={showCreate} onHide={() => setShowCreate(false)}>

                <Modal.Header closeButton>

                    <Modal.Title className="d-flex flex-column gap-1">
                        <span>Новий матеріал</span>
                        <span className="text-muted fw-normal fs-6">
                            Лише картка матеріалу (без зміни залишку)
                        </span>
                    </Modal.Title>

                </Modal.Header>

                <Modal.Body>

                    <p className="text-muted small mb-3">
                        Тут зберігаються номенклатура (назва типу), колір, одиниця та опис для довідника й
                        техкарт. Кількість на складі після збереження не змінюється — для цього
                        використовуйте «Закупки» або оприбуткування на «Складі».
                    </p>

                    <Form>

                        <Form.Group className="mb-3">
                            <Form.Label>Категорія матеріалу</Form.Label>
                            <Form.Select
                                value={createTypeCategory}
                                onChange={(e) => {
                                    const cat = e.target.value;
                                    setCreateTypeCategory(cat);
                                    setForm((prev) => ({
                                        ...prev,
                                        unit: defaultMaterialFormUnitForCategory(cat),
                                    }));
                                }}
                            >
                                {MATERIAL_CATEGORY_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Тканина, фурнітура тощо — як у закупці. Назву конкретної позиції вводите
                                полем нижче.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Назва матеріалу (номенклатура)</Form.Label>

                            <Form.Control
                                list="materials-create-type-name-hints"
                                value={form.typeName}
                                onChange={(e) =>
                                    setForm({ ...form, typeName: e.target.value })
                                }
                                placeholder="Наприклад: Блискавка №18, Кулір білий преміум…"
                                autoComplete="off"
                            />
                            <datalist id="materials-create-type-name-hints">
                                {typesForCreate.map((t) => (
                                    <option key={t._id} value={t.name} />
                                ))}
                            </datalist>

                        </Form.Group>


                        {!isPrintsEmbroideryCategory(createTypeCategory) ? (
                            <Form.Group className="mb-3">

                                <Form.Label>Колір</Form.Label>

                                <Form.Control
                                    value={form.color}
                                    onChange={(e)=>
                                        setForm({...form,color:e.target.value})
                                    }
                                />

                            </Form.Group>
                        ) : null}


                        <Form.Group className="mb-3">

                            <Form.Label>Одиниця</Form.Label>

                            <MaterialUnitFormSelect
                                value={form.unit}
                                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                            />

                        </Form.Group>

                        <div className="d-flex flex-wrap gap-3 mb-3">
                            <Form.Group className="flex-grow-1" style={{ minWidth: "10rem" }}>
                                <Form.Label>Ціна на картці, ₴ / од.</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={0}
                                    step="any"
                                    inputMode="decimal"
                                    value={form.priceUAH}
                                    onChange={(e) =>
                                        setForm({ ...form, priceUAH: e.target.value })
                                    }
                                    placeholder="необов'язково"
                                />
                            </Form.Group>
                            <Form.Group className="flex-grow-1" style={{ minWidth: "10rem" }}>
                                <Form.Label>Ціна на картці, $ / од.</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={0}
                                    step="any"
                                    inputMode="decimal"
                                    value={form.priceUSD}
                                    onChange={(e) =>
                                        setForm({ ...form, priceUSD: e.target.value })
                                    }
                                    placeholder="необов'язково"
                                />
                            </Form.Group>
                        </div>

                        {!isPrintsEmbroideryCategory(createTypeCategory) ? (
                        <Form.Group className="mb-3">
                            <Form.Label>Властивості (ключ : значення)</Form.Label>
                            <PropertyPairsModalEditor
                                rows={form.propertyPairRows}
                                onChange={(propertyPairRows) => setForm({ ...form, propertyPairRows })}
                                category={createTypeCategory}
                                unit={form.unit}
                            />
                        </Form.Group>
                        ) : null}


                        <Form.Group className="mt-3">

                            <Form.Label>Коментар</Form.Label>

                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={form.note}
                                onChange={(e)=>
                                    setForm({...form,note:e.target.value})
                                }
                            />

                        </Form.Group>

                    </Form>

                </Modal.Body>

                <Modal.Footer>

                    <Button onClick={createMaterial}>
                        Зберегти
                    </Button>

                </Modal.Footer>

            </Modal>

            <Modal show={Boolean(editMaterial)} onHide={closeEditMaterial}>
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex flex-column gap-1">
                        <span>Редагувати матеріал</span>
                        <span className="text-muted fw-normal fs-6">
                            Картка в довіднику; залишок на складі тут не змінюється
                        </span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Назва в довіднику (редагування написання)</Form.Label>
                            <Form.Control
                                value={editForm.typeName}
                                onChange={(e) =>
                                    setEditForm({ ...editForm, typeName: e.target.value })
                                }
                                placeholder="Назва в довіднику типів"
                            />
                        </Form.Group>
                        {editMaterial &&
                        !isPrintsEmbroideryCategory(materialRowCategory(editMaterial, types)) ? (
                        <Form.Group className="mb-3">
                            <Form.Label>Колір</Form.Label>
                            <Form.Control
                                value={editForm.color}
                                onChange={(e) =>
                                    setEditForm({ ...editForm, color: e.target.value })
                                }
                            />
                        </Form.Group>
                        ) : null}
                        <Form.Group className="mb-3">
                            <Form.Label>Одиниця</Form.Label>
                            <MaterialUnitFormSelect
                                value={editForm.unit}
                                onChange={(e) => {
                                    const next = String(e.target.value || "");
                                    const isM =
                                        String(next || "").trim().toLowerCase() === "м";
                                    setEditForm((prev) => ({
                                        ...prev,
                                        unit: next,
                                        unitPriceBasis: isM ? prev.unitPriceBasis : "stock",
                                    }));
                                }}
                            />
                        </Form.Group>
                        {editMaterial &&
                        !isPrintsEmbroideryCategory(materialRowCategory(editMaterial, types)) ? (
                        <Form.Group className="mb-3">
                            <Form.Label>Властивості (ключ : значення)</Form.Label>
                            <PropertyPairsModalEditor
                                rows={editForm.propertyPairRows}
                                onChange={(propertyPairRows) =>
                                    setEditForm({ ...editForm, propertyPairRows })
                                }
                                category={
                                    editMaterial
                                        ? materialRowCategory(editMaterial, types)
                                        : "FABRIC"
                                }
                                unit={editForm.unit}
                            />
                        </Form.Group>
                        ) : null}
                        {editFormWindingMeters != null &&
                        editFormWindingMeters > 0 &&
                        String(editForm.unit || "").trim().toLowerCase() === "м" ? (
                            <Form.Group className="mb-3">
                                <Form.Label>Як трактувати поле ціни (₴/$) на картці</Form.Label>
                                <Form.Select
                                    value={editForm.unitPriceBasis || "stock"}
                                    onChange={(e) =>
                                        setEditForm({
                                            ...editForm,
                                            unitPriceBasis: e.target.value,
                                        })
                                    }
                                    aria-label="База ціни для обліку та техкарт"
                                >
                                    <option value="stock">
                                        За 1 метр (типово після закупівлі бобінами через «Закупки»)
                                    </option>
                                    <option value="bobbin">
                                        За 1 бобіну — перерахувати ₴/м = ціна ÷ намотку (якщо раніше
                                        закупили в «шт»)
                                    </option>
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    При «За бобіну» у техкарті та списанні використовується ціна за
                                    метр: <strong>{editFormWindingMeters} м</strong> на бобіну.
                                </Form.Text>
                            </Form.Group>
                        ) : null}
                        <Form.Group className="mb-3">
                            <Form.Label>Коментар</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={editForm.note}
                                onChange={(e) =>
                                    setEditForm({ ...editForm, note: e.target.value })
                                }
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-between">
                    <Button
                        variant="outline-danger"
                        onClick={() => {
                            if (editMaterial) {
                                confirmDeleteMaterial(editMaterial);
                            }
                        }}
                    >
                        Видалити…
                    </Button>
                    <div className="d-flex gap-2">
                        <Button variant="secondary" onClick={closeEditMaterial}>
                            Скасувати
                        </Button>
                        <Button variant="primary" onClick={saveEditMaterial}>
                            Зберегти
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

        </div>

    );
}